// src/components/AddProductModal.tsx
import React, { useState, useEffect } from "react";
import axios from "axios";
import { Product } from "../pages/ProductContent/Product";

interface Props {
  onClose: () => void;
  isEdit?: boolean;
  initialData?: Product;
}

const AddProductModal: React.FC<Props> = ({
  onClose,
  isEdit = false,
  initialData,
}) => {
  const [form, setForm] = useState<Product>({
    no: "",
    name: "",
    unit: "",
    mrp: 0,
    category: "",
    itemClass: "",
    hsncode: "",
    gstpercentage: 0,
  });

  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<Partial<Record<keyof Product, string>>>(
    {}
  );
  const [touched, setTouched] = useState<
    Partial<Record<keyof Product, boolean>>
  >({});

  useEffect(() => {
    if (isEdit && initialData) {
      setForm(initialData);
    }
  }, [isEdit, initialData]);

  // Validation functions
  const validateField = (
    name: keyof Product,
    value: string | number
  ): string => {
    switch (name) {
      case "no":
        if (!value || (typeof value === "string" && value.trim().length < 2)) {
          return "Product code is required (min 2 characters)";
        }
        if (typeof value === "string" && value.trim().length > 50) {
          return "Product code cannot exceed 50 characters";
        }
        break;

      case "name":
        if (!value || (typeof value === "string" && value.trim().length < 2)) {
          return "Product name must be at least 2 characters long";
        }
        if (typeof value === "string" && value.trim().length > 100) {
          return "Product name cannot exceed 100 characters";
        }
        break;

      

      case "unit":
        if (!value || (typeof value === "string" && value.trim().length < 1)) {
          return "Unit is required";
        }
        if (
          typeof value === "string" &&
          !["Mtrs", "Nos", "PCS", "Manual"].includes(value.trim())
        ) {
          return "Please select a valid unit";
        }
        break;

      case "category":
        if (!value || (typeof value === "string" && value.trim().length < 2)) {
          return "Category must be at least 2 characters long";
        }
        break;

      case "hsncode":
        if (!value || (typeof value === "string" && value.trim().length < 4)) {
          return "HSN code must be at least 4 characters long";
        }
        if (typeof value === "string" && !/^\d+$/.test(value.trim())) {
          return "HSN code must contain only numbers";
        }
        break;

      case "mrp":
        if (!value || Number(value) <= 0) {
          return "MRP must be greater than 0";
        }
        if (Number(value) > 999999) {
          return "MRP cannot exceed 999,999";
        }
        break;

      case "gstpercentage":
        if (Number(value) < 0 || Number(value) > 100) {
          return "GST percentage must be between 0 and 100";
        }
        break;

      default:
        break;
    }
    return "";
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<Record<keyof Product, string>> = {};

    Object.keys(form).forEach((key) => {
      const fieldKey = key as keyof Product;
      const value = form[fieldKey] ?? ""; // or 0 for numbers
      const error = validateField(fieldKey, value);
      if (error) {
        newErrors[fieldKey] = error;
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    const fieldName = name as keyof Product;

    // Update form value
    const newValue =
      name === "mrp" || name === "gstpercentage" ? Number(value) : value;
    setForm({ ...form, [name]: newValue });

    // Mark field as touched
    setTouched({ ...touched, [name]: true });

    // Validate field in real-time
    if (touched[fieldName]) {
      const error = validateField(fieldName, newValue);
      setErrors({ ...errors, [name]: error });
    }
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const { name } = e.target;
    const fieldName = name as keyof Product;

    // Mark field as touched
    setTouched({ ...touched, [name]: true });

    // Validate field
   const error = validateField(fieldName, form[fieldName] ?? '');
    setErrors({ ...errors, [name]: error });
  };

  const handleSelectBlur = (e: React.FocusEvent<HTMLSelectElement>) => {
  const fieldName = e.target.name as keyof Product;
  const error = validateField(fieldName, form[fieldName] ?? '');
  setErrors({ ...errors, [fieldName]: error });
};

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate entire form
    if (!validateForm()) {
      // Mark all fields as touched to show errors
      const allTouched = Object.keys(form).reduce((acc, key) => {
        acc[key as keyof Product] = true;
        return acc;
      }, {} as Partial<Record<keyof Product, boolean>>);
      setTouched(allTouched);
      return;
    }

    setIsLoading(true);

    const token = localStorage.getItem("token");

    if (!token) {
      alert("You're not logged in");
      setIsLoading(false);
      return;
    }

    const config = {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    };

    try {
      if (isEdit && initialData?._id) {
        await axios.put(
          `${process.env.REACT_APP_API_URL || 'http://localhost:5000/api/v1'}/products/${initialData._id}`,
          form,
          config
        );
      } else {
        await axios.post(
          `${process.env.REACT_APP_API_URL || 'http://localhost:5000/api/v1'}/products`,
          form,
          config
        );
      }
      onClose();
    } catch (err) {
      console.error(err);
      alert(isEdit ? "Failed to update product" : "Failed to add product");
    } finally {
      setIsLoading(false);
    }
  };

  const inputClasses = (hasError: boolean) =>
    `w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 transition-all duration-200 bg-gray-50 hover:bg-white ${
      hasError
        ? "border-red-300 focus:ring-red-500 focus:border-red-500"
        : "border-gray-200 focus:ring-blue-500 focus:border-transparent"
    }`;

  const selectClasses = (hasError: boolean) =>
    `w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 transition-all duration-200 bg-gray-50 hover:bg-white cursor-pointer ${
      hasError
        ? "border-red-300 focus:ring-red-500 focus:border-red-500"
        : "border-gray-200 focus:ring-blue-500 focus:border-transparent"
    }`;

  const labelClasses = "block text-sm font-medium text-gray-700 mb-2";
  const errorClasses = "text-red-500 text-xs mt-1 block";

  const unitOptions = ["Mtrs", "Nos", "PCS", "Manual"];

  const hasErrors = Object.values(errors).some((error) => error !== "");

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center p-4 z-50">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-100">
          <div className="flex items-center justify-between">
            <h3 className="text-xl font-semibold text-gray-800">
              {isEdit ? "Edit Product" : "Add New Product"}
            </h3>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors duration-200"
            >
              <svg
                className="w-5 h-5 text-gray-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="px-6 py-4 space-y-4 mx-0.3">
          <div>
            <label className={labelClasses}>Product Code</label>
            <input
              type="text"
              name="no"
              placeholder="Enter product code (e.g., IM100164)"
              className={inputClasses(!!errors.no)}
              value={form.no}
              onChange={handleChange}
              onBlur={handleBlur}
              required
            />
            {errors.no && <span className={errorClasses}>{errors.no}</span>}
          </div>

          <div>
            <label className={labelClasses}>Product Name</label>
            <input
              type="text"
              name="name"
              placeholder="Enter product name"
              className={inputClasses(!!errors.name)}
              value={form.name}
              onChange={handleChange}
              onBlur={handleBlur}
              required
            />
            {errors.name && <span className={errorClasses}>{errors.name}</span>}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelClasses}>Unit</label>
              <select
                name="unit"
                className={selectClasses(!!errors.unit)}
                value={form.unit}
                onChange={handleChange}
                onBlur={handleSelectBlur}
                required
              >
                <option value="">Select unit</option>
                {unitOptions.map((unit) => (
                  <option key={unit} value={unit}>
                    {unit}
                  </option>
                ))}
              </select>
              {errors.unit && (
                <span className={errorClasses}>{errors.unit}</span>
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelClasses}>Category</label>
              <input
                type="text"
                name="category"
                placeholder="Product category"
                className={inputClasses(!!errors.category)}
                value={form.category}
                onChange={handleChange}
                onBlur={handleBlur}
                required
              />
              {errors.category && (
                <span className={errorClasses}>{errors.category}</span>
              )}
            </div>
            
            <div>
              <label className={labelClasses}>Item Class</label>
              <input
                type="text"
                name="itemClass"
                placeholder="Item class (optional)"
                className={inputClasses(!!errors.itemClass)}
                value={form.itemClass || ""}
                onChange={handleChange}
                onBlur={handleBlur}
              />
              {errors.itemClass && (
                <span className={errorClasses}>{errors.itemClass}</span>
              )}
            </div>
          </div>

          <div>
            <label className={labelClasses}>HSN Code</label>
            <input
              type="text"
              name="hsncode"
              placeholder="Enter HSN code"
              className={inputClasses(!!errors.hsncode)}
              value={form.hsncode}
              onChange={handleChange}
              onBlur={handleBlur}
              required
            />
            {errors.hsncode && (
              <span className={errorClasses}>{errors.hsncode}</span>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelClasses}>MRP (₹)</label>
              <input
                type="number"
                name="mrp"
                placeholder="0.00"
                className={inputClasses(!!errors.mrp)}
                value={form.mrp}
                onChange={handleChange}
                onBlur={handleBlur}
                min="0"
                step="0.01"
                required
              />
              {errors.mrp && <span className={errorClasses}>{errors.mrp}</span>}
            </div>
            <div>
              <label className={labelClasses}>GST (%)</label>
              <input
                type="number"
                name="gstpercentage"
                placeholder="0"
                className={inputClasses(!!errors.gstpercentage)}
                value={form.gstpercentage}
                onChange={handleChange}
                onBlur={handleBlur}
                min="0"
                max="100"
                required
              />
              {errors.gstpercentage && (
                <span className={errorClasses}>{errors.gstpercentage}</span>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-3 pt-6 border-t border-gray-100">
            <button
              type="button"
              className="px-6 py-2.5 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg font-medium transition-colors duration-200"
              onClick={onClose}
              disabled={isLoading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className={`px-6 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-all duration-200 flex items-center gap-2 ${
                isLoading || hasErrors ? "opacity-70 cursor-not-allowed" : ""
              }`}
              disabled={isLoading || hasErrors}
            >
              {isLoading && (
                <svg
                  className="animate-spin h-4 w-4"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
              )}
              {isLoading
                ? "Saving..."
                : isEdit
                ? "Update Product"
                : "Add Product"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddProductModal;
